import { useEffect, useRef, useState } from "react";
import { message } from "antd";

import { useAppSelector } from "../../../store/reduxHooks";
import {
  DocumentKkb,
  DocumentToSign,
  DocumentType,
} from "../../../store/slice/requestAppSlise/requestAppSlise";
import {
  useVerificationDocsSignatureMutation,
} from "../../../store/api/application.api";
import { ModesDocumentVerificationAfterSigning } from "../../../store/api/types/applicationApiTypes";
import { getSignedFile } from "../../../utils/utilities/crypto-pro/getSignedFile";
import { API_CENTER } from "../../../services/api/api";
import { SELECT_REQUEST_BANK_APPLICATION_STATUS } from "../../../store/selectors/requestAppSelector";
import { isModesDocumentVerificationAfterSigning } from "../../../store/api/typeGuards/ApplicationApiTypeGuards";
import { ApplicationDocumentsToSign } from "../types/types";
import { getDocumentsForSigningFromServer } from "../helpers/getDocumentsForSigningFromServer";
import { verificationModesAfterSign } from "../../../constants/verificationModesAfterSign";

export type ModalView =
  | "confirmText"
  | "selectSign"
  | "ecpAnimation"
  | "finish";

const apiStart = new API_CENTER();

/**
 * Логика модалки подписания
 * @param isShowDefault - открыта ли модалка по-умолчанию
 * @returns {Object} Стор для модалки подписания
 * @property {boolean} isModalVisible - открыта ли модалка
 * @property {string} modalState - текущая вкладка модалки (Пользовательское соглашение, выбор подписи, процесс подписания с анимацией)
 * @property {Object} modalHandler - функции обработчики событий модалки
 */
export const useClientSignModalState = (
  isShowDefault = false,
  afterAccessSigningDocs?: () => void,
) => {
  const abortRef = useRef(false); // реф для отмены подписания (если true, подписание прерывается)
  const bankStatus = useAppSelector(SELECT_REQUEST_BANK_APPLICATION_STATUS);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(isShowDefault);
  const [modalView, setModalView] = useState<ModalView>("confirmText");

  const [verificationDocument] = useVerificationDocsSignatureMutation();

  /**
   * Подписание документа с типом DocumentType
   *
   * @async
   * @param {DocumentType<DocumentKkb>} doc
   * @param {number} applicationBankId
   * @param {string} thumbprint
   * @returns {*}
   */
  const signDocumentType = async (
    doc: DocumentType<DocumentKkb>,
    applicationBankId: number,
    thumbprint: string,
  ) => {
    if (doc.document && doc.document.sign_file?.length === 0) {
      message.success(`Подпись документа "${doc.name}"`);
      // Получаем файл документа

      const docFile: Blob = await apiStart.configAxios(
        `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/file/${doc.document.id}`,
        {
          method: "GET",
          responseType: "blob",
        },
        true,
        false,
      );

      // Подписание файла через КриптоПро
      const signedDocFile = await getSignedFile({
        fileName: doc.name,
        file: docFile,
        thumbprint: thumbprint,
      });

      // Данные для сервера
      const formData = new FormData();
      if (signedDocFile) {
        formData.append("file", signedDocFile);
      }

      // Отправка подписанного файла на сервер
      await apiStart.configAxios(
        `auth/sign/client-sign-file/application-bank/${applicationBankId}/file/${doc.document.id}`,
        {
          method: "POST",
          data: formData,
        },
        true,
      );
    }
  };

  /**
   * Подпись документа с типом DocumentToSign
   *
   * @async
   * @param {DocumentToSign} doc
   * @param {number} applicationBankId
   * @param {string} thumbprint
   * @returns {*}
   */
  const signDocumentToSign = async (
    doc: DocumentToSign,
    applicationBankId: number,
    thumbprint: string,
  ) => {
    if (doc && doc.sign_file?.length === 0) {
      message.success(`Подпись документа "${doc.name}"`);
      // Получаем файл документа

      const docFile: Blob = await apiStart.configAxios(
        `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/file/${doc.id}`,
        {
          method: "GET",
          responseType: "blob",
        },
        true,
        false,
      );

      // Подписание файла через КриптоПро
      const signedDocFile = await getSignedFile({
        fileName: doc.name,
        file: docFile,
        thumbprint: thumbprint,
      });

      // Данные для сервера
      const formData = new FormData();
      if (signedDocFile) {
        formData.append("file", signedDocFile);
      }

      // Отправка подписанного файла на сервер
      await apiStart.configAxios(
        `auth/sign/client-sign-file/application-bank/${applicationBankId}/file/${doc.id}`,
        {
          method: "POST",
          data: formData,
        },
        true,
      );
    }
  };

  /**
   * подпись документов с помощью криптоПро и отправка на сервер
   *
   * @async
   * @param {DocumentType<DocumentKkb>[] | DocumentToSign[]} documents
   * @param {number} applicationBankId
   * @param {string} thumbprint
   * @returns {*}
   */
  const signDocumentFilesCryptoPro = async (
    documents: DocumentType<DocumentKkb>[] | DocumentToSign[],
    applicationBankId: number,
    thumbprint: string,
  ) => {
    for await (const doc of documents) {
      if (abortRef.current) {
        throw new Error("Отмена подписания");
      }

      if (Object.hasOwn(doc, "type_doc")) {
        const rawDoc = doc as DocumentToSign;
        await signDocumentToSign(rawDoc, applicationBankId, thumbprint);
      } else {
        const rawDoc = doc as DocumentType<DocumentKkb>;
        await signDocumentType(rawDoc, applicationBankId, thumbprint);
      }
    }
  };

  /**
   * Подпись документов с помощью крипто про и верификация документов на сервере
   *
   * @async
   * @param {number} applicationId
   * @param {string} thumbprint
   * @returns {*}
   */
  const signDocumentsCryptoProAndVerificate = async (
    applicationId: number,
    thumbprint: string,
    verificationModeParam?: ModesDocumentVerificationAfterSigning,
  ) => {
    const docsData = await getDocumentsForSigningFromServer(
      thumbprint,
      applicationId,
    );

    message.info(`Подпись документов заявки №${applicationId}`);

    if (docsData) {
      await signDocumentFilesCryptoPro(
        docsData.documents,
        applicationId,
        thumbprint,
      );

      // переменная, определяющая в каком режиме будет проводиться верификация документов
      // (для первого подписания или повторного)
      const verificationMode =
        verificationModeParam || verificationModesAfterSign[bankStatus.type];

      if (
        verificationMode !== undefined &&
        isModesDocumentVerificationAfterSigning(verificationMode)
      ) {
        await verificationDocument({
          applicationBankId: applicationId,
          mode: verificationMode,
        }).unwrap();
      }
    }
  };

  const modalHandler = {
    open: () => {
      setIsModalVisible(true);
    },
    close: () => {
      setIsModalVisible(false);
      setModalView("confirmText");
      abortRef.current = true;
    },
    // Смена страницы модалки
    setView: (view: ModalView) => {
      setModalView(view);
    },
    // Подписание документов всех заявок
    signAllDocuments: async (
      docsData: ApplicationDocumentsToSign[],
      thumbprint: string,
    ) => {
      setModalView("ecpAnimation");
      try {
        for await (const docData of docsData) {
          if (abortRef.current) {
            break;
          }

          await signDocumentsCryptoProAndVerificate(
            docData.application_bank_id,
            thumbprint,
          );
        }
        if (!abortRef.current) {
          message.success("Документы успешно подписаны");
        }

        setModalView("finish");
      } catch (error) {
        modalHandler.close();
        message.error("Подписание остановлено");
      }
    },
    // Подписание документов одной заявки
    signDocumentsByAppId: async (applicationId: number, thumbprint: string) => {
      setModalView("ecpAnimation");
      try {
        await signDocumentsCryptoProAndVerificate(applicationId, thumbprint);

        if (!abortRef.current) {
          if (afterAccessSigningDocs) {
            afterAccessSigningDocs();
          }

          message.success("Документы успешно подписаны");
        }
        modalHandler.close();
      } catch (error) {
        modalHandler.close();
        message.error("Подписание остановлено");
      }
    },
    // Отмена подписания
    cancel: () => {
      if (modalView === "ecpAnimation") {
        const confrim = confirm(
          "Вы уверены, что хотите остановить подписание документов?",
        );

        if (confrim === true) {
          modalHandler.close();
          abortRef.current = true;
        }
      } else {
        modalHandler.close();
        abortRef.current = true;
      }
    },
  };

  return {
    modalView,
    isModalVisible,
    modalHandler,
    signDocumentsCryptoProAndVerificate,
  };
};
